/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router';

import { SignedUrl } from '@/commons/protocols';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useGet } from '@/net/commons';
import { useUserContext } from '@/pages/Root';

const TraceLink = (props: {
    projectSlug: string;
    suiteSlug: string;
    executionId: string;
    runId: string;
    isFinished: boolean;
    attempt?: number;
}) => {
    const { projectSlug, suiteSlug, executionId, runId, isFinished, attempt = 1 } = props;
    const { isAdmin, isImpersonated } = useUserContext().user;
    const hasAdminPermissions = isAdmin || isImpersonated;

    const { data: dataTraces, error: errorTraces } = useGet<SignedUrl[]>(
        isFinished
            ? // eslint-disable-next-line max-len
              `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/trace?attempt=${attempt}`
            : null,
    );
    const { data: dataChains, error: errorChainLogs } = useGet<SignedUrl>(
        hasAdminPermissions && isFinished
            ? // eslint-disable-next-line max-len
              `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/chainlogs?attempt=${attempt}`
            : null,
    );
    const { data: dataRunJob, error: errorRunJob } = useGet<SignedUrl>(
        hasAdminPermissions && isFinished
            ? // eslint-disable-next-line max-len
              `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/input-job`
            : null,
    );

    const PW_URL = 'https://trace.playwright.dev/?trace=';

    return (
        <div className="flex flex-row">
            {process.env.NODE_ENV !== 'development' && hasAdminPermissions && (
                <div className="flex items-baseline mr-2">
                    <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        // eslint-disable-next-line max-len
                        to={`https://logs.betterstack.com/team/220949/tail?q=runId%3D${runId}%20level%3Derror`}
                        className="underline text-muted-foreground text-sm mr-1"
                    >
                        Logs
                    </Link>
                    <ExternalLinkIcon size={8} />
                </div>
            )}
            {isFinished && !errorTraces && dataTraces && (
                <div className="flex items-center mr-2">
                    <Popover>
                        <PopoverTrigger>
                            <div className="flex items-center">
                                <span className="underline text-muted-foreground text-sm mr-1">Traces</span>
                                <ExternalLinkIcon size={8} />
                            </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                            <div className="flex flex-col gap-2">
                                {dataTraces.map((trace, index) => (
                                    <Link
                                        key={index}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        to={
                                            process.env.NODE_ENV === 'development'
                                                ? trace.url
                                                : PW_URL + encodeURIComponent(trace.url)
                                        }
                                        className="underline text-muted-foreground text-sm hover:text-primary"
                                    >
                                        {trace.fileName}
                                    </Link>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            )}
            {hasAdminPermissions && isFinished && !errorChainLogs && dataChains && (
                <div className="flex items-center">
                    <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to={dataChains.url}
                        className="underline text-muted-foreground text-sm mr-1"
                    >
                        Chainlogs
                    </Link>
                    <ExternalLinkIcon size={8} />
                </div>
            )}
            {hasAdminPermissions && isFinished && !errorRunJob && dataRunJob && (
                <div className="flex items-center ml-2">
                    <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to={dataRunJob.url}
                        className="underline text-muted-foreground text-sm mr-1"
                    >
                        Repro
                    </Link>
                    <ExternalLinkIcon size={8} />
                </div>
            )}
        </div>
    );
};

export default TraceLink;
